// Shipment Costings service - moved from database.js
import { 
  collection, 
  doc, 
  getDocs, 
  getDoc,
  addDoc, 
  updateDoc, 
  query, 
  where, 
  serverTimestamp
} from 'firebase/firestore';
import { db } from '../../firebase';
import { validateCostingData, sanitizeCostingData } from '../../utils/shipment-costing-validation';
import { migrateLegacyCostingRecord, needsMigration } from '../../utils/shipment-costing-migration';
import { DEFAULT_COSTING_DATA } from '../../types/shipment-costing-types';

// Note: This import will be updated once journalEntriesService is moved
// import { journalEntriesService } from '../accounting/journal-entries';

export const shipmentCostingsService = {
  async getShipmentCostings(companyId) {
    const costingsRef = collection(db, 'shipmentCostings');
    const q = query(costingsRef, where('companyId', '==', companyId));
    const snapshot = await getDocs(q);
    
    // Process and migrate records if needed
    return snapshot.docs.map(doc => {
      const data = { id: doc.id, ...doc.data() };
      
      // Check if migration is needed
      if (needsMigration(data)) {
        return migrateLegacyCostingRecord(data);
      }
      
      return data;
    });
  },

  async updateCosting(costingId, costingData) {
    try {
      // First, get the existing costing data to preserve values not being updated
      const costingRef = doc(db, 'shipmentCostings', costingId);
      const existingDoc = await getDoc(costingRef);
      
      if (!existingDoc.exists()) {
        throw new Error('Costing record not found');
      }
      
      const existingData = existingDoc.data();
      
      // Enhance costing data with separate PetroApp and manual fuel tracking
      const enhancedCostingData = {
        ...costingData,
        // Preserve reference-based fields
        appliedFuelTransactionIds: costingData.appliedFuelTransactionIds || existingData.appliedFuelTransactionIds || []
      };
      
      // If updating fuel costs, ensure proper separation and totals
      if (costingData.petroAppFuelAmount !== undefined || costingData.manualFuelAmount !== undefined) {
        // Preserve existing values if not being updated
        const petroAppAmount = costingData.petroAppFuelAmount !== undefined 
          ? costingData.petroAppFuelAmount 
          : (existingData.petroAppFuelAmount || 0);
        const manualAmount = costingData.manualFuelAmount !== undefined 
          ? costingData.manualFuelAmount 
          : (existingData.manualFuelAmount || 0);
        
        // Preserve existing VAT breakdown values if not being updated
        const petroAppBaseAmount = costingData.petroAppFuelBaseAmount !== undefined
          ? costingData.petroAppFuelBaseAmount
          : (existingData.petroAppFuelBaseAmount || 0);
        const petroAppVATAmount = costingData.petroAppFuelVATAmount !== undefined
          ? costingData.petroAppFuelVATAmount
          : (existingData.petroAppFuelVATAmount || 0);
        const manualBaseAmount = costingData.manualFuelBaseAmount !== undefined
          ? costingData.manualFuelBaseAmount
          : (existingData.manualFuelBaseAmount || 0);
        const manualVATAmount = costingData.manualFuelVATAmount !== undefined
          ? costingData.manualFuelVATAmount
          : (existingData.manualFuelVATAmount || 0);
        
        // Calculate combined totals
        enhancedCostingData.totalFuelAmount = petroAppAmount + manualAmount;
        enhancedCostingData.totalFuelBaseAmount = petroAppBaseAmount + manualBaseAmount;
        enhancedCostingData.totalFuelVATAmount = petroAppVATAmount + manualVATAmount;
        
        // Update legacy fields for backward compatibility
        enhancedCostingData.fuel = enhancedCostingData.totalFuelAmount;
        enhancedCostingData.fuelAmount = enhancedCostingData.totalFuelAmount;
        enhancedCostingData.fuelBaseAmount = enhancedCostingData.totalFuelBaseAmount;
        enhancedCostingData.fuelVATAmount = enhancedCostingData.totalFuelVATAmount;
      }
    
      // Calculate VAT breakdown for manual fuel if not already calculated
      if (enhancedCostingData.manualFuelAmount > 0 && 
          (enhancedCostingData.manualFuelBaseAmount === 0 || enhancedCostingData.manualFuelVATAmount === 0)) {
        const { calculateVATBreakdown } = await import('../../utils/vat-calculations');
        const vatBreakdown = calculateVATBreakdown(
          enhancedCostingData.manualFuelAmount, 
          enhancedCostingData.manualFuelIncludesVAT !== false
        );
        enhancedCostingData.manualFuelBaseAmount = vatBreakdown.baseAmount;
        enhancedCostingData.manualFuelVATAmount = vatBreakdown.vatAmount;
      }

      // Calculate VAT breakdown for PetroApp fuel if not already calculated
      if (enhancedCostingData.petroAppFuelAmount > 0 && 
          (enhancedCostingData.petroAppFuelBaseAmount === 0 || enhancedCostingData.petroAppFuelVATAmount === 0)) {
        const { calculateVATBreakdown } = await import('../../utils/vat-calculations');
        const vatBreakdown = calculateVATBreakdown(
          enhancedCostingData.petroAppFuelAmount, 
          enhancedCostingData.petroAppFuelIncludesVAT !== false
        );
        enhancedCostingData.petroAppFuelBaseAmount = vatBreakdown.baseAmount;
        enhancedCostingData.petroAppFuelVATAmount = vatBreakdown.vatAmount;
      }

      // Re-sync totals & legacy fields in case the late VAT breakdown recalcs above
      // overwrote manual/petroApp base/VAT amounts.
      enhancedCostingData.totalFuelBaseAmount =
        (enhancedCostingData.petroAppFuelBaseAmount || 0) + (enhancedCostingData.manualFuelBaseAmount || 0);
      enhancedCostingData.totalFuelVATAmount =
        (enhancedCostingData.petroAppFuelVATAmount || 0) + (enhancedCostingData.manualFuelVATAmount || 0);
      enhancedCostingData.fuelBaseAmount = enhancedCostingData.totalFuelBaseAmount;
      enhancedCostingData.fuelVATAmount = enhancedCostingData.totalFuelVATAmount;

      // Validate and sanitize the enhanced costing data
      const sanitizedData = sanitizeCostingData(enhancedCostingData);
      const validation = validateCostingData(sanitizedData);

      if (!validation.isValid) {
        throw new Error(`Invalid costing data: ${validation.errors.join(', ')}`);
      }

      // Use the existing costingRef from above
      const updateResult = await updateDoc(costingRef, { 
        ...sanitizedData, 
        updatedAt: serverTimestamp() 
      });
    
    // If this is a cost update (not just adding invoice reference), create journal entry
    if (costingData.fuelCost || costingData.maintenanceCost || costingData.driverSalary || 
        costingData.commissionCost || costingData.otherCosts || 
        costingData.petroAppFuelAmount || costingData.manualFuelAmount) {
      
      try {
        // Note: This will be updated once journalEntriesService is moved
        // Get the full costing data and shipment data, then create journal entry for shipment costs
        // const costingDoc = await getDoc(costingRef);
        // const fullCostingData = costingDoc.data();
        // const shipmentDoc = await getDoc(doc(db, 'shipments', fullCostingData.shipmentId));
        // const shipmentData = shipmentDoc.data();
        // await journalEntriesService.createShipmentCostingJournalEntry(
        //   fullCostingData.companyId,
        //   { id: fullCostingData.shipmentId, ...shipmentData },
        //   { ...fullCostingData, ...costingData },
        //   fullCostingData.createdBy || 'system'
        // );
        
        console.log('✅ Journal entry created for shipment costing update');
        
      } catch (journalError) {
        console.error('❌ Error creating journal entry for shipment costing:', journalError);
        // Don't fail the costing update if journal entry fails
      }
    }
    
      return updateResult;
    } catch (error) {
      console.error('Error updating costing:', error);
      throw error;
    }
  },

  async addCosting(costingData) {
    // Enhance costing data with separate PetroApp and manual fuel tracking
    const enhancedCostingData = {
      ...costingData,
      
      // PetroApp Fuel Costs (from imported transactions) - Reference-based
      petroAppFuelAmount: costingData.petroAppFuelAmount || 0,
      petroAppFuelIncludesVAT: costingData.petroAppFuelIncludesVAT !== undefined ? costingData.petroAppFuelIncludesVAT : true,
      petroAppFuelBaseAmount: costingData.petroAppFuelBaseAmount || 0,
      petroAppFuelVATAmount: costingData.petroAppFuelVATAmount || 0,
      appliedFuelTransactionIds: costingData.appliedFuelTransactionIds || [], // Reference-based: Store only IDs
      
      // Manual Fuel Costs (user-entered)
      manualFuelAmount: costingData.manualFuelAmount || 0,
      manualFuelIncludesVAT: costingData.manualFuelIncludesVAT !== undefined ? costingData.manualFuelIncludesVAT : true,
      manualFuelBaseAmount: costingData.manualFuelBaseAmount || 0,
      manualFuelVATAmount: costingData.manualFuelVATAmount || 0,
      
      // Combined Fuel Totals (for backward compatibility and reporting)
      totalFuelAmount: (costingData.petroAppFuelAmount || 0) + (costingData.manualFuelAmount || 0),
      totalFuelBaseAmount: (costingData.petroAppFuelBaseAmount || 0) + (costingData.manualFuelBaseAmount || 0),
      totalFuelVATAmount: (costingData.petroAppFuelVATAmount || 0) + (costingData.manualFuelVATAmount || 0),
      
      // Legacy fields for backward compatibility
      fuel: (costingData.petroAppFuelAmount || 0) + (costingData.manualFuelAmount || 0),
      fuelAmount: (costingData.petroAppFuelAmount || 0) + (costingData.manualFuelAmount || 0),
      fuelBaseAmount: (costingData.petroAppFuelBaseAmount || 0) + (costingData.manualFuelBaseAmount || 0),
      fuelVATAmount: (costingData.petroAppFuelVATAmount || 0) + (costingData.manualFuelVATAmount || 0)
    };
    
    // Calculate VAT breakdown for manual fuel if not already calculated
    if (enhancedCostingData.manualFuelAmount > 0 && 
        (enhancedCostingData.manualFuelBaseAmount === 0 || enhancedCostingData.manualFuelVATAmount === 0)) {
      const { calculateVATBreakdown } = await import('../../utils/vat-calculations');
      const vatBreakdown = calculateVATBreakdown(
        enhancedCostingData.manualFuelAmount, 
        enhancedCostingData.manualFuelIncludesVAT !== false
      );
      enhancedCostingData.manualFuelBaseAmount = vatBreakdown.baseAmount;
      enhancedCostingData.manualFuelVATAmount = vatBreakdown.vatAmount;
    }

    // Calculate VAT breakdown for PetroApp fuel if not already calculated
    if (enhancedCostingData.petroAppFuelAmount > 0 && 
        (enhancedCostingData.petroAppFuelBaseAmount === 0 || enhancedCostingData.petroAppFuelVATAmount === 0)) {
      const { calculateVATBreakdown } = await import('../../utils/vat-calculations');
      const vatBreakdown = calculateVATBreakdown(
        enhancedCostingData.petroAppFuelAmount, 
        enhancedCostingData.petroAppFuelIncludesVAT !== false
      );
      enhancedCostingData.petroAppFuelBaseAmount = vatBreakdown.baseAmount;
      enhancedCostingData.petroAppFuelVATAmount = vatBreakdown.vatAmount;
    }

    // Validate and sanitize the enhanced costing data
    const sanitizedData = sanitizeCostingData(enhancedCostingData);
    const validation = validateCostingData(sanitizedData);
    
    if (!validation.isValid) {
      throw new Error(`Invalid costing data: ${validation.errors.join(', ')}`);
    }
    
    const costingsRef = collection(db, 'shipmentCostings');
    const docRef = await addDoc(costingsRef, { 
      ...sanitizedData, 
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp()
    });
    
    // Create journal entry for the new costing
    try {
      // Note: This will be updated once journalEntriesService is moved
      // Get shipment information and create journal entry for shipment costs
      // const shipmentDoc = await getDoc(doc(db, 'shipments', costingData.shipmentId));
      // const shipmentData = shipmentDoc.data();
      // await journalEntriesService.createShipmentCostingJournalEntry(
      //   costingData.companyId,
      //   { id: costingData.shipmentId, ...shipmentData },
      //   costingData,
      //   costingData.createdBy || 'system'
      // );
      
      console.log('✅ Journal entry created for new shipment costing');
      
    } catch (journalError) {
      console.error('❌ Error creating journal entry for new shipment costing:', journalError);
      // Don't fail the costing creation if journal entry fails
    }
    
    return { id: docRef.id, ...sanitizedData };
  },

  async getCostingsByCompany(companyId) {
    const costingsRef = collection(db, 'shipmentCostings');
    const q = query(costingsRef, where('companyId', '==', companyId));
    const snapshot = await getDocs(q);
    
    // Process and migrate records if needed
    return snapshot.docs.map(doc => {
      const data = { id: doc.id, ...doc.data() };
      
      // Check if migration is needed
      if (needsMigration(data)) {
        return migrateLegacyCostingRecord(data);
      }
      
      return data;
    });
  },
  async getCostingByShipment(shipmentId) {
    const costingsRef = collection(db, 'shipmentCostings');
    const q = query(costingsRef, where('shipmentId', '==', shipmentId));
    const snapshot = await getDocs(q);
    const costingDoc = snapshot.docs[0];
    
    if (!costingDoc) {
      return null;
    }
    
    const data = { id: costingDoc.id, ...costingDoc.data() };
    
    // Check if migration is needed
    if (needsMigration(data)) {
      return migrateLegacyCostingRecord(data);
    }
    
    return data;
  },
  async autoCreateCosting({ shipmentId, companyId, createdBy, driverId = null }) {
    // Create an empty costing record with enhanced VAT-aware structure
    const costingData = {
      shipmentId,
      companyId,
      driverId, // Add driver ID for commission tracking
      
      // Enhanced VAT-aware fields
      ...DEFAULT_COSTING_DATA,
      
      // Legacy fields for backward compatibility
      fuel: 0,
      maintenance: 0,
      commission: 0,
      
      // Metadata
      createdBy,
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp()
    };
    
    const costingsRef = collection(db, 'shipmentCostings');
    const docRef = await addDoc(costingsRef, costingData);
    return docRef.id;
  },

  /**
   * Migrates a single costing record to the enhanced format
   * @param {string} costingId - ID of the costing record to migrate
   * @param {string} userId - ID of the user performing the migration
   * @returns {Object} Migration result
   */
  async migrateCostingRecord(costingId, userId) {
    try {
      const costingRef = doc(db, 'shipmentCostings', costingId);
      const costingDoc = await getDocs(query(collection(db, 'shipmentCostings'), where('__name__', '==', costingId)));
      
      if (costingDoc.empty) {
        throw new Error('Costing record not found');
      }
      
      const originalData = { id: costingId, ...costingDoc.docs[0].data() };
      
      if (!needsMigration(originalData)) {
        return { success: true, skipped: true, message: 'Record already migrated' };
      }
      
      const migratedData = migrateLegacyCostingRecord(originalData);
      migratedData.vatCalculatedBy = userId;
      
      await updateDoc(costingRef, migratedData);
      
      return { 
        success: true, 
        recordId: costingId,
        originalData,
        migratedData 
      };
      
    } catch (error) {
      return { 
        success: false, 
        recordId: costingId,
        error: error.message 
      };
    }
  },

  // Special update method that bypasses strict VAT validation for transaction removal
  async updateCostingWithoutValidation(costingId, costingData) {
    try {
      const costingRef = doc(db, 'shipmentCostings', costingId);
      const existingDoc = await getDoc(costingRef);
      
      if (!existingDoc.exists()) {
        throw new Error('Costing record not found');
      }
      
      // Sanitize data but skip strict validation
      const sanitizedData = sanitizeCostingData(costingData);
      
      // Update directly without validation
      const updateResult = await updateDoc(costingRef, { 
        ...sanitizedData, 
        updatedAt: serverTimestamp() 
      });
      
      console.log(`✅ Updated costing ${costingId} without validation (transaction removal)`);
      return updateResult;
    } catch (error) {
      console.error('Error updating costing without validation:', error);
      throw error;
    }
  }
}; 